import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const CookieConsent = () => {
  const { language } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [isEU, setIsEU] = useState(false);

  // EU/EEA country codes
  const EU_COUNTRIES = [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 
    'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 
    'SE', 'GB', 'IS', 'LI', 'NO', 'CH'
  ];

  useEffect(() => {
    const checkConsent = async () => {
      // Check if user already gave consent
      const consent = localStorage.getItem('cookie_consent');
      if (consent) {
        updateGoogleConsent(consent === 'accepted');
        return;
      }

      // Check if user is from EU
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        const userCountry = data.country_code;
        
        if (EU_COUNTRIES.includes(userCountry)) {
          setIsEU(true);
          setShowBanner(true);
          // Set initial consent to denied for EU users
          updateGoogleConsent(false);
        } else {
          // Non-EU users - auto-accept
          updateGoogleConsent(true);
        }
      } catch (error) {
        console.error('Error checking location:', error);
        // If we can't determine location, show banner to be safe
        setShowBanner(true);
      }
    };

    checkConsent();
  }, []);

  const updateGoogleConsent = (granted) => {
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': granted ? 'granted' : 'denied',
        'ad_storage': granted ? 'granted' : 'denied',
        'ad_user_data': granted ? 'granted' : 'denied',
        'ad_personalization': granted ? 'granted' : 'denied'
      });
    }
  };

  const handleAccept = () => {
    localStorage.setItem('cookie_consent', 'accepted');
    updateGoogleConsent(true);
    setShowBanner(false);
  };

  const handleReject = () => {
    localStorage.setItem('cookie_consent', 'rejected');
    updateGoogleConsent(false);
    setShowBanner(false);
  };

  if (!showBanner) return null;

  const content = {
    en: {
      message: 'We use cookies to analyze website traffic and optimize your experience. By accepting our use of cookies, your data will be aggregated with all other user data.',
      accept: 'Accept',
      reject: 'Reject',
      learnMore: 'Learn More'
    },
    ar: {
      message: 'نستخدم ملفات تعريف الارتباط لتحليل حركة المرور على الموقع وتحسين تجربتك. من خلال قبول استخدامنا لملفات تعريف الارتباط، سيتم تجميع بياناتك مع جميع بيانات المستخدمين الآخرين.',
      accept: 'قبول',
      reject: 'رفض',
      learnMore: 'معرفة المزيد'
    }
  };

  const text = content[language] || content.en;

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.95)',
      color: 'white',
      padding: '20px',
      zIndex: 99999,
      boxShadow: '0 -2px 10px rgba(0,0,0,0.3)',
      backdropFilter: 'blur(10px)',
      borderTop: '2px solid rgba(102, 126, 234, 0.5)'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: window.innerWidth < 768 ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '20px'
      }}>
        <div style={{ flex: 1 }}>
          <p style={{ 
            margin: 0, 
            fontSize: '14px', 
            lineHeight: '1.5',
            textAlign: language === 'ar' ? 'right' : 'left'
          }}>
            {text.message}{' '}
            <a 
              href={`/${language}/privacy-policy`} 
              style={{ 
                color: '#667eea', 
                textDecoration: 'underline',
                fontWeight: '500'
              }}
            >
              {text.learnMore}
            </a>
          </p>
        </div>
        <div style={{ 
          display: 'flex', 
          gap: '10px',
          flexShrink: 0
        }}>
          <button
            onClick={handleReject}
            style={{
              padding: '10px 24px',
              backgroundColor: 'transparent',
              border: '2px solid rgba(255,255,255,0.3)',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontFamily: '"Roboto", sans-serif'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(255,255,255,0.1)';
              e.target.style.borderColor = 'rgba(255,255,255,0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.borderColor = 'rgba(255,255,255,0.3)';
            }}
          >
            {text.reject}
          </button>
          <button
            onClick={handleAccept}
            style={{
              padding: '10px 24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              fontFamily: '"Roboto", sans-serif'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
            }}
          >
            {text.accept}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
